/**
 * CGStepPure
 *
 * Pure presentational component for rendering cadence steps.
 * Works in both Next.js and Electron by receiving all data via props.
 *
 * This is a recursive component - it renders itself for child steps.
 */

import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import { CGDelayStepPure } from './CGDelayStepPure';
import { CGWaitForStepPure, renderLeafChainAsDayCard } from './CGWaitForStepPure';
import type { EditStepPure, CreateStepData } from './CGAddStepPure';
import type { CadenceGraphNodesPure, StepUpdateInputPure } from './types';
import type { SiftConfig } from './CTSContentEditingPure';

// ============================================================================
// Types
// ============================================================================

export type CGStepPureProps = {
  first?: boolean;
  /**
   * The id of the PREVIOUS day's `delay` node. Threaded through the
   * recursive render so that a non-first delay node knows where to
   * insert a brand-new day BEFORE itself: passing this as `afterStepId`
   * routes through the server's "add after delay → splice in a new
   * delay node" branch, which correctly creates a new day group with
   * the requested wait. Undefined for the first node.
   */
  prevDelayNodeId?: string;
  /**
   * URL to the cadence template page (e.g. `/templates/{templateId}`).
   * Threaded through the recursive render so day cards can surface a
   * "Not in template" / "See in template" link in their header.
   */
  templateHref?: string;
  nodeId: string;
  nodes: CadenceGraphNodesPure;
  msUpToNow: number;
  hasSalesNav?: boolean | null;
  /** True when the contact is already a LinkedIn connection — disables Connect in the picker. */
  alreadyConnectedToContact?: boolean;
  /** Reminder editor — list of available reminder actions. */
  reminderActions?: ReminderAction[];
  /** Reminder editor — true while the actions list is loading. */
  reminderActionsLoading?: boolean;
  /** Reminder editor — callback to create a new reminder action by name. */
  onCreateReminder?: (name: string) => Promise<void>;
  /** Reminder editor — true while a new reminder action is being created. */
  reminderCreating?: boolean;
  /** Reminder editor — fires when the reminder editor reports an error. */
  onReminderError?: (message: string) => void;
  editingStep?: EditStepPure;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  contactId?: string;
  onChangeType: (nodeId: string, type: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onCreateStep: (data: CreateStepData) => unknown;
  onUpdateStep: (stepId: string, data: StepUpdateInputPure) => unknown;
  onToggleInmailOpen: (nodeId: string, onlyOpenLink: boolean) => void;
  markFinished: (nodeId: string) => void;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => React.ReactNode;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  /**
   * Display-only mode: render the full step plan but hide every edit
   * affordance (click-to-edit rows, delay pills, add-step pickers,
   * mark-done buttons). Used by hosts that don't wire the step
   * mutations, e.g. the extension sidepanel. The callback props stay
   * required — readOnly hosts pass no-ops.
   */
  readOnly?: boolean;
};

// ============================================================================
// Component
// ============================================================================

export function CGStepPure({
  first,
  prevDelayNodeId,
  templateHref,
  msUpToNow,
  onCreateStep,
  nodeId,
  nodes,
  hasSalesNav,
  alreadyConnectedToContact,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  editingStep,
  setEditingStep,
  variables,
  documents,
  contactId,
  onChangeType,
  onDeleteNode,
  onUpdateStep,
  onToggleInmailOpen,
  markFinished,
  renderSiftConditionBuilder,
  voiceEnabled,
  voiceCallingEnabled,
  readOnly,
}: CGStepPureProps) {
  const node = nodes[nodeId];
  if (node == null) return null;

  const now = Date.now();

  // Render props for recursive rendering
  const renderCGStep = (props: {
    nodeId: string;
    first?: boolean;
    msUpToNow: number;
    prevDelayNodeId?: string;
  }) => (
    <CGStepPure
      {...props}
      nodes={nodes}
      hasSalesNav={hasSalesNav}
      alreadyConnectedToContact={alreadyConnectedToContact}
      reminderActions={reminderActions}
      reminderActionsLoading={reminderActionsLoading}
      onCreateReminder={onCreateReminder}
      reminderCreating={reminderCreating}
      onReminderError={onReminderError}
      editingStep={editingStep}
      setEditingStep={setEditingStep}
      variables={variables}
      documents={documents}
      contactId={contactId}
      onChangeType={onChangeType}
      onDeleteNode={onDeleteNode}
      onCreateStep={onCreateStep}
      onUpdateStep={onUpdateStep}
      onToggleInmailOpen={onToggleInmailOpen}
      markFinished={markFinished}
      renderSiftConditionBuilder={renderSiftConditionBuilder}
      templateHref={templateHref}
      voiceEnabled={voiceEnabled}
      voiceCallingEnabled={voiceCallingEnabled}
      readOnly={readOnly}
    />
  );

  switch (node.type) {
    case 'wait_for_connect': {
      const finishedDate =
        node.state === 'finished' || node.state === 'skipped'
          ? (node.completedOn?.getTime() ?? msUpToNow)
          : Math.max(node.canRunOn?.getTime() ?? msUpToNow, now);
      return (
        <CGWaitForStepPure
          first={first}
          nodeId={nodeId}
          nodes={nodes}
          msUpToNow={finishedDate}
          hasSalesNav={hasSalesNav}
          alreadyConnectedToContact={alreadyConnectedToContact}
          editingStep={editingStep}
          setEditingStep={setEditingStep}
          variables={variables}
          documents={documents}
          contactId={contactId}
          onChangeType={onChangeType}
          onDeleteNode={onDeleteNode}
          onCreateStep={onCreateStep}
          onUpdateStep={onUpdateStep}
          markFinished={markFinished}
          renderCGStep={renderCGStep}
          renderSiftConditionBuilder={renderSiftConditionBuilder}
          reminderActions={reminderActions}
          reminderActionsLoading={reminderActionsLoading}
          onCreateReminder={onCreateReminder}
          reminderCreating={reminderCreating}
          onReminderError={onReminderError}
          templateHref={templateHref}
          voiceEnabled={voiceEnabled}
          voiceCallingEnabled={voiceCallingEnabled}
          readOnly={readOnly}
        />
      );
    }
    case 'delay': {
      const finishedDate =
        node.state === 'finished' || node.state === 'skipped'
          ? (node.completedOn?.getTime() ?? msUpToNow)
          : // offset the delay because the canRunOn on the delay step does not have the delay offset in it
            Math.max(node.canRunOn?.getTime() ?? msUpToNow, now - node.delay);

      return (
        <CGDelayStepPure
          first={first}
          prevDelayNodeId={prevDelayNodeId}
          nodeId={nodeId}
          nodes={nodes}
          msUpToNow={finishedDate}
          hasSalesNav={hasSalesNav}
          alreadyConnectedToContact={alreadyConnectedToContact}
          reminderActions={reminderActions}
          reminderActionsLoading={reminderActionsLoading}
          onCreateReminder={onCreateReminder}
          reminderCreating={reminderCreating}
          onReminderError={onReminderError}
          editingStep={editingStep}
          setEditingStep={setEditingStep}
          variables={variables}
          documents={documents}
          contactId={contactId}
          onChangeType={onChangeType}
          onDeleteNode={onDeleteNode}
          onCreateStep={onCreateStep}
          onUpdateStep={onUpdateStep}
          markFinished={markFinished}
          renderCGStep={renderCGStep}
          renderSiftConditionBuilder={renderSiftConditionBuilder}
          templateHref={templateHref}
          voiceEnabled={voiceEnabled}
          voiceCallingEnabled={voiceCallingEnabled}
          readOnly={readOnly}
        />
      );
    }
    default:
      // Bare leaf at the cadence root (no leading delay) — render it
      // as a synthetic day card. `parentStepId` falls back to this
      // node's own id; it's only used as an anchor in the empty-branch
      // case (startId == null), which can't happen here since startId
      // IS this node's id.
      return renderLeafChainAsDayCard({
        nodes,
        startId: nodeId,
        parentStepId: nodeId,
        msUpToNow,
        editingStep,
        setEditingStep,
        variables,
        documents,
        renderCGStep,
        renderSiftConditionBuilder,
        reminderActions,
        reminderActionsLoading,
        onCreateReminder,
        reminderCreating,
        onReminderError,
        alreadyConnectedToContact,
        templateHref,
        voiceEnabled,
        voiceCallingEnabled,
        readOnly,
        onCreateStep,
        onUpdateStep,
        onDeleteStep: onDeleteNode,
        markFinished,
      });
  }
}
